<script setup lang="ts">
import { useNotificationStore } from '../stores/notificationStore';
import { TransitionGroup } from 'vue';

const store = useNotificationStore();

const getIcon = (type: string) => {
  switch (type) {
    case 'success':
      return `<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />`;
    case 'error':
      return `<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />`;
    case 'warning':
      return `<path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />`;
    default:
      return `<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />`;
  }
};

const getBackgroundColor = (type: string) => {
  switch (type) {
    case 'success': return 'bg-green-50';
    case 'error': return 'bg-red-50';
    case 'warning': return 'bg-amber-50';
    default: return 'bg-blue-50';
  }
};

const getTextColor = (type: string) => {
  switch (type) {
    case 'success': return 'text-green-800';
    case 'error': return 'text-red-800';
    case 'warning': return 'text-amber-800';
    default: return 'text-blue-800';
  }
};

const getIconColor = (type: string) => {
  switch (type) {
    case 'success': return 'text-green-400';
    case 'error': return 'text-red-400';
    case 'warning': return 'text-amber-400';
    default: return 'text-blue-400';
  }
};
</script>

<template>
  <div class="fixed top-4 left-1/2 -translate-x-1/2 z-50 space-y-2 w-full max-w-sm">
    <TransitionGroup
      enter-active-class="transform ease-out duration-300 transition"
      enter-from-class="translate-y-2 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-for="notification in store.notifications"
        :key="notification.id"
        :class="[
          'w-full shadow-lg rounded-lg pointer-events-auto overflow-hidden mx-auto',
          getBackgroundColor(notification.type)
        ]"
      >
        <div class="p-4">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <svg
                class="h-6 w-6"
                :class="getIconColor(notification.type)"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
                v-html="getIcon(notification.type)"
              />
            </div>
            <div class="ml-3 w-0 flex-1">
              <p
                :class="[
                  'text-sm font-medium',
                  getTextColor(notification.type)
                ]"
              >
                {{ notification.message }}
              </p>
            </div>
            <div class="ml-4 flex-shrink-0 flex">
              <button
                @click="store.removeNotification(notification.id)"
                class="inline-flex text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <span class="sr-only">Close</span>
                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>